import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { useTranslation } from '@/lib/i18n';
import type { OrderSummary } from '@/lib/api-admin/types';
import {
  extractSummaryCharge,
  formatChargeAmount,
  formatKrwLedger,
} from '../lib/chargeMoney';
import { memberNameOf, ordererNameOf } from '../lib/ordererName';

export type OrderSummaryExtended = OrderSummary & {
  buyerName?: string | null;
  buyerEmail?: string | null;
  ordererName?: string | null;
  ordererMobile?: string | null;
  // [pay-currency] 해외몰 외화 청구 — be-snapshot 발행 계약(OrderSummaryResponse.charge,
  // ChargeMoney: amount=Toss-native decimal STRING). 미배포/KRW 주문은 키 생략 → KRW 원장
  // 단독 표기(현행 동작 보존).
  charge?: { amount: string; currencyCode: string } | null;
  // QA-455 — 국가몰(BCP-47 코드, 목록 응답 ALWAYS)·배송비(BE 목록 응답 추가 요청 — 없으면 "-").
  mall?: string | null;
  shippingFee?: { amount: number; currencyCode: string } | null;
  // QA-536 노출 항목 — BE 목록 응답 추가 요청(BO-TO-BE-2026-10-27-order-list-columns-qa536). 없으면 "-".
  paymentMethod?: string | null;
  paymentMethodLabel?: string | null;
  buyerMobile?: string | null;
  totalQuantity?: number | null;
  totalDiscountAmount?: MoneyLike | null;
  totalOrderAmount?: MoneyLike | null;
  payableAmount?: MoneyLike | null;
  representativeItem: OrderSummary['representativeItem'] & {
    selfProductCode?: string | null;
    unitPrice?: MoneyLike | null;
  };
};

type MoneyLike = { amount: number; currencyCode: string };

const Dash = () => <span style={{ color: 'var(--content-muted)' }}>-</span>;

/** 결제수단 표시 — BE 라벨 우선, 없으면 코드 → 한글. */
export const paymentMethodLabelOf = (o: Pick<OrderSummaryExtended, 'paymentMethod' | 'paymentMethodLabel'>): string | null =>
  o.paymentMethodLabel?.trim() || (o.paymentMethod ? labelOf(PAYMENT_METHOD_LABELS, o.paymentMethod) : null);

/** 주문자 연락처 — BE buyerMobile, 없으면 ordererMobile. QA-536 국내 번호는 010-1234-5678 형식. */
export const ordererMobileOf = (o: Pick<OrderSummaryExtended, 'buyerMobile' | 'ordererMobile'>): string | null =>
  formatPhoneNumber(o.buyerMobile?.trim() || o.ordererMobile?.trim() || null);
import {
  ORDER_SORTABLE_COLUMNS,
  toggleOrderSort,
  type OrderSort,
} from '@/stores/useOrderFiltersStore';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui';
import { OrderStatusPill } from './OrderStatusPill';
import { labelOf, PAYMENT_METHOD_LABELS } from '@/lib/enumLabels';
import { formatPhoneNumber } from '@/lib/format/phone';
import {
  ORDER_COLUMN_LABELS,
  useOrderColumnPrefs,
  type ToggleableOrderColumn,
} from '@/stores/useOrderColumnPrefs';
import { ORDER_MALL_LABELS } from '../lib/orderMall';
import { CnyReference } from './CnyReference';

export interface OrdersTableProps {
  items: ReadonlyArray<OrderSummaryExtended>;
  sort: OrderSort;
  onSortChange: (sort: OrderSort) => void;
  selectedIds: ReadonlyArray<string>;
  onToggleRow: (orderId: string) => void;
  onToggleAll: (ids: ReadonlyArray<string>) => void;
}

export const parseOrderSort = (sort: OrderSort): { id: string; dir: 'asc' | 'desc' } => {
  const [id, dir] = sort.split(',');
  return { id: id ?? 'orderedAt', dir: dir === 'asc' ? 'asc' : 'desc' };
};

export const formatMoney = (m: { amount: number; currencyCode: string }): string =>
  `${m.amount.toLocaleString()} ${m.currencyCode}`;

export const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const OrdersTable = ({
  items,
  sort,
  onSortChange,
  selectedIds,
  onToggleRow,
  onToggleAll,
}: OrdersTableProps) => {
  const { t } = useTranslation('orders');
  const navigate = useNavigate();
  const { id: activeSortId, dir: activeSortDir } = parseOrderSort(sort);

  const allIds = useMemo(() => items.map((i) => i.orderId), [items]);
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  // QA-536 노출 항목 — 칸 정의는 한곳(아래 맵), 보일 칸·순서는 useOrderColumnPrefs.
  const visibleColumns = useOrderColumnPrefs((s) => s.visibleColumns);

  const columns = useMemo<ColumnDef<OrderSummaryExtended>[]>(() => {
    const money = (m: MoneyLike | null | undefined) =>
      m ? <span className="tabular-nums">{m.currencyCode === 'KRW' ? formatKrwLedger(m.amount) : formatMoney(m)}</span> : <Dash />;
    const defs: Record<ToggleableOrderColumn, ColumnDef<OrderSummaryExtended>> = {
      orderedAt: {
        id: 'orderedAt',
        header: () => t('orders.columns.orderedAt'),
        accessorFn: (row) => formatDateTime(row.orderedAt),
      },
      mall: {
        // QA-455 — 국가별로 구분해 보기(필터는 상단 [국가몰]).
        id: 'mall',
        header: () => t('orders.columns.mall', '국가몰'),
        accessorFn: (row) => labelOf(ORDER_MALL_LABELS, row.mall ?? undefined),
      },
      paymentMethod: {
        id: 'paymentMethod',
        header: () => ORDER_COLUMN_LABELS.paymentMethod,
        cell: ({ row }) => {
          const label = paymentMethodLabelOf(row.original);
          return label ? <span>{label}</span> : <Dash />;
        },
      },
      paymentAmount: {
        id: 'paymentAmount',
        header: () => t('orders.columns.paymentAmount'),
        // [pay-currency] 외화 청구 주문은 청구 통화가 주 표기, KRW 원장은 보조(혼동 방지 병기).
        // KRW 주문은 ₩ 단독 — 청구 필드 미배포/누락 시에도 동일하게 동작(additive 안전).
        cell: ({ row }) => {
          const charge = extractSummaryCharge(row.original);
          const ledger = row.original.paymentAmount;
          const ledgerText =
            ledger.currencyCode === 'KRW' ? formatKrwLedger(ledger.amount) : formatMoney(ledger);
          if (!charge) return <span className="tabular-nums">{ledgerText}</span>;
          return (
            <div data-testid="order-charge-cell">
              <div className="tabular-nums text-sm font-medium" style={{ color: 'var(--content-strong)' }}>
                {formatChargeAmount(charge.amount, charge.currencyCode)}
                <CnyReference amount={charge.amount} currencyCode={charge.currencyCode} mall={row.original.mall} />
              </div>
              <div className="tabular-nums text-xs" style={{ color: 'var(--content-muted)' }}>
                {ledgerText}
              </div>
            </div>
          );
        },
      },
      ordererName: {
        id: 'ordererName',
        header: () => ORDER_COLUMN_LABELS.ordererName,
        cell: ({ row }) => {
          const name = ordererNameOf(row.original);
          const member = memberNameOf(row.original);
          return (
            <div>
              <span
                className="block max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium"
                style={{ color: 'var(--content-strong)' }}
                title={name ?? '-'}
              >
                {name ?? '-'}
              </span>
              {member ? (
                <span className="block max-w-[140px] truncate text-xs" style={{ color: 'var(--content-muted)' }} title={member} data-testid="order-member-name">
                  회원명 {member}
                </span>
              ) : null}
            </div>
          );
        },
      },
      ordererEmail: {
        id: 'ordererEmail',
        header: () => ORDER_COLUMN_LABELS.ordererEmail,
        cell: ({ row }) => {
          const email = row.original.buyerEmail;
          return email ? <span className="block max-w-[180px] truncate text-xs" title={email}>{email}</span> : <Dash />;
        },
      },
      ordererMobile: {
        id: 'ordererMobile',
        header: () => ORDER_COLUMN_LABELS.ordererMobile,
        cell: ({ row }) => {
          const mobile = ordererMobileOf(row.original);
          return mobile ? <span className="whitespace-nowrap text-xs">{mobile}</span> : <Dash />;
        },
      },
      product: {
        id: 'representativeItem',
        header: () => ORDER_COLUMN_LABELS.product,
        cell: ({ row }) => {
          const rep = row.original.representativeItem;
          const extra = row.original.additionalItemCount;
          const name = rep.productName || '-';
          return (
            <span className="flex items-center gap-1">
              <span
                className="block max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                title={name}
              >
                {name}
              </span>
              {extra > 0 ? (
                <span className="shrink-0 text-[color:var(--content-muted)]">
                  {t('orders.list.additionalCount', { count: extra })}
                </span>
              ) : null}
            </span>
          );
        },
      },
      quantity: {
        // 주문 단위 목록은 주문 총 수량(BE totalQuantity).
        id: 'quantity',
        header: () => ORDER_COLUMN_LABELS.quantity,
        cell: ({ row }) =>
          typeof row.original.totalQuantity === 'number' ? (
            <span className="tabular-nums">{row.original.totalQuantity}</span>
          ) : (
            <Dash />
          ),
      },
      selfProductCode: {
        // 주문 단위 목록은 대표 상품의 자체상품코드.
        id: 'selfProductCode',
        header: () => ORDER_COLUMN_LABELS.selfProductCode,
        cell: ({ row }) => {
          const code = row.original.representativeItem.selfProductCode;
          return code ? <span className="text-xs">{code}</span> : <Dash />;
        },
      },
      unitPrice: {
        // 주문 단위 목록은 대표 상품 판매가.
        id: 'unitPrice',
        header: () => ORDER_COLUMN_LABELS.unitPrice,
        cell: ({ row }) => money(row.original.representativeItem.unitPrice),
      },
      totalDiscountAmount: {
        id: 'totalDiscountAmount',
        header: () => ORDER_COLUMN_LABELS.totalDiscountAmount,
        cell: ({ row }) => money(row.original.totalDiscountAmount),
      },
      totalOrderAmount: {
        id: 'totalOrderAmount',
        header: () => ORDER_COLUMN_LABELS.totalOrderAmount,
        cell: ({ row }) => money(row.original.totalOrderAmount),
      },
      shippingFee: {
        // QA-455 — 배송비(주문 전체). BE 목록 응답에 shippingFee 가 오기 전에는 "-".
        id: 'shippingFee',
        header: () => ORDER_COLUMN_LABELS.shippingFee,
        cell: ({ row }) => {
          const fee = row.original.shippingFee;
          if (!fee) return <Dash />;
          return (
            <span className="tabular-nums" data-testid="order-shipping-fee-cell">
              {fee.currencyCode === 'KRW' ? formatKrwLedger(fee.amount) : formatMoney(fee)}
            </span>
          );
        },
      },
      payableAmount: {
        id: 'payableAmount',
        header: () => ORDER_COLUMN_LABELS.payableAmount,
        // order #124 — 실 결제금액은 목록 paymentAmount 와 같은 값(BE 회신, payableAmount 는 따로 없음).
        cell: ({ row }) => money(row.original.payableAmount ?? row.original.paymentAmount),
      },
      fulfillmentType: {
        id: 'fulfillmentType',
        header: () => t('orders.columns.fulfillmentType'),
        accessorFn: (row) => t(`orders.fulfillmentType.${row.fulfillmentType}`),
      },
      status: {
        id: 'displayStatus',
        header: () => t('orders.columns.status'),
        cell: ({ row }) => (
          <OrderStatusPill
            displayStatus={row.original.displayStatus}
            displayLabelCode={
              (row.original as { displayLabelCode?: string | null }).displayLabelCode
            }
            orderId={row.original.orderId}
          />
        ),
      },
    };
    return [
      {
        id: 'orderNo',
        header: () => t('orders.columns.orderNo'),
        cell: ({ row }) => (
          <span
            className="block max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap"
            title={row.original.orderNo}
          >
            {row.original.orderNo}
          </span>
        ),
      },
      ...visibleColumns.map((c) => defs[c]),
    ];
  }, [t, visibleColumns]);

  const table = useReactTable({
    data: items as OrderSummaryExtended[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (items.length === 0) {
    return (
      <p
        className="py-8 text-center text-sm text-[color:var(--content-muted)]"
        data-testid="orders-empty"
      >
        {t('orders.list.empty')}
      </p>
    );
  }

  return (
    <Table data-testid="orders-table">
      <TableHeader style={{ background: 'var(--surface-1)' }}>
        <TableRow>
          <TableHead scope="col" style={{ width: '44px' }}>
            <input
              type="checkbox"
              aria-label={t('orders.list.selectAll')}
              checked={allChecked}
              onChange={() => onToggleAll(allChecked ? [] : allIds)}
            />
          </TableHead>
          {table.getHeaderGroups()[0]?.headers.map((header) => {
            const isSortable = ORDER_SORTABLE_COLUMNS.has(header.column.id);
            const isActive = activeSortId === header.column.id;
            return (
              // QA-536 — 칸을 늘려도 머리글이 한 글자씩 세로로 꺾이지 않게(표는 가로 스크롤).
              <TableHead key={header.id} scope="col" className="whitespace-nowrap">
                {isSortable ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1"
                    onClick={() => onSortChange(toggleOrderSort(sort, header.column.id))}
                    aria-label={t('orders.list.sortBy', {
                      column: t(`orders.columns.${header.column.id}`),
                    })}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <span aria-hidden="true">
                      {isActive ? (activeSortDir === 'asc' ? '↑' : '↓') : ''}
                    </span>
                  </button>
                ) : (
                  flexRender(header.column.columnDef.header, header.getContext())
                )}
              </TableHead>
            );
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => {
          const order = row.original;
          const isSelected = selectedIds.includes(order.orderId);
          return (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              style={{ height: 'var(--density-compact-row-h)' }}
              onClick={(e) => {
                if ((e.target as HTMLElement).tagName === 'INPUT') return;
                navigate(`/orders/${order.orderId}`);
              }}
              data-selected={isSelected ? 'true' : 'false'}
              data-testid="orders-row"
              data-order-id={order.orderId}
            >
              <TableCell className="px-3">
                <input
                  type="checkbox"
                  aria-label={t('orders.list.selectRow', { orderNo: order.orderNo })}
                  checked={isSelected}
                  onChange={() => onToggleRow(order.orderId)}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="whitespace-nowrap">
                  {flexRender(
                    cell.column.columnDef.cell ?? ((ctx) => ctx.getValue() as string),
                    cell.getContext(),
                  )}
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
